import type { FormatedProductPrice } from "@dransay/api";
import type { DeliveryMethodKind } from "@dransay/api/models";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFetcher } from "react-router";
import {
	buildExclusiveProductsFetchUrl,
	excludeCurrentProductFromExclusiveList,
	getExclusiveProductsFromRefetchResponse,
	shouldReuseLoaderExclusiveProducts,
} from "./exclusive-products-pdp.logic";

interface CuratedProductsFetcherResponse {
	products?: FormatedProductPrice[];
	error?: string;
}

interface UsePdpExclusiveProductsOptions {
	productId: string;
	initialProducts: FormatedProductPrice[];
	initialVendorId: string | undefined;
	initialDeliveryMethodKind: DeliveryMethodKind | undefined;
	vendorId: string | undefined;
	deliveryMethodKind: DeliveryMethodKind | undefined;
}

export function usePdpExclusiveProducts({
	productId,
	initialProducts,
	initialVendorId,
	initialDeliveryMethodKind,
	vendorId,
	deliveryMethodKind,
}: UsePdpExclusiveProductsOptions) {
	const fetcher = useFetcher<CuratedProductsFetcherResponse>();
	const excludeCurrentProduct = useMemo(
		() => (items: FormatedProductPrice[]) =>
			excludeCurrentProductFromExclusiveList(items, productId),
		[productId],
	);
	const shouldReuseLoaderProducts = useMemo(
		() =>
			shouldReuseLoaderExclusiveProducts({
				initialVendorId,
				initialDeliveryMethodKind,
				displayedVendorId: vendorId,
				displayedDeliveryMethodKind: deliveryMethodKind,
			}),
		[initialDeliveryMethodKind, initialVendorId, vendorId, deliveryMethodKind],
	);
	const [products, setProducts] = useState(() =>
		shouldReuseLoaderProducts ? excludeCurrentProduct(initialProducts) : [],
	);
	const lastFetchedVendorKey = useRef<string | null>(null);
	const loadingVendorKey = useRef<string | null>(null);
	const lastResolvedVendorKey = useRef<string | null>(null);
	const vendorKey = `${vendorId ?? ""}:${deliveryMethodKind ?? ""}`;
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset when PDP product or loader data changes
	useEffect(() => {
		setProducts(
			shouldReuseLoaderProducts ? excludeCurrentProduct(initialProducts) : [],
		);
		lastFetchedVendorKey.current = null;
		loadingVendorKey.current = null;
		lastResolvedVendorKey.current = null;
	}, [productId, initialProducts]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: fetcher is stable enough; vendorKey drives refetch
	useEffect(() => {
		if (lastFetchedVendorKey.current === vendorKey) {
			return;
		}
		if (lastFetchedVendorKey.current === null) {
			lastFetchedVendorKey.current = vendorKey;
			if (shouldReuseLoaderProducts || !vendorId) {
				return;
			}
			fetcher.load(
				buildExclusiveProductsFetchUrl(vendorId, deliveryMethodKind),
			);
			return;
		}
		if (!vendorId) {
			lastFetchedVendorKey.current = vendorKey;
			setProducts([]);
			return;
		}
		lastFetchedVendorKey.current = vendorKey;
		fetcher.load(buildExclusiveProductsFetchUrl(vendorId, deliveryMethodKind));
	}, [vendorId, deliveryMethodKind, vendorKey, shouldReuseLoaderProducts]);
	useEffect(() => {
		if (
			fetcher.state === "loading" &&
			lastFetchedVendorKey.current === vendorKey
		) {
			loadingVendorKey.current = vendorKey;
		}
	}, [fetcher.state, vendorKey]);
	useEffect(() => {
		if (fetcher.state !== "idle" || !fetcher.data) {
			return;
		}
		if (
			lastFetchedVendorKey.current !== vendorKey ||
			loadingVendorKey.current !== vendorKey ||
			lastResolvedVendorKey.current === vendorKey
		) {
			return;
		}
		lastResolvedVendorKey.current = vendorKey;
		setProducts(
			getExclusiveProductsFromRefetchResponse(fetcher.data.products, productId),
		);
	}, [fetcher.state, fetcher.data, productId, vendorKey]);
	const isLoading = fetcher.state === "loading";
	return { products, isLoading };
}
